import api from "@/api";
import deepClone from "@/utils/deep-clone";

import { alphabetically } from "@/utils/sort-object";

const defaultElement = {
    type: "",
    name: "",
    description: "",
    location: {
        type: "link",
        url: "",
        key: "",
    },
    open: {
        enabled: false,
        targeting: {
            all: true,
            localTeams: [],
            users: [],
        },
    },
    taggedUsers: [],
    questions: [],
    frozen: false,
    public: false,
};

const filters = [
    {
        key: "type",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }
            return list.filter(item => item[this.key] === this.value);
        },
    },
    {
        key: "createdBy",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }
            return list.filter(item => item[this.key]?._id === this.value);
        },
    },
    {
        key: "usedInPaths",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }

            if (this.value === "none") {
                return list.filter(item => item[this.key]?.length === 0);
            }

            return list.filter(item => item[this.key]?.find(path => path.commonId === this.value));
        },
    },
];

const state = {
    details: {},
    list: [],
    filters,
};

const getters = {
    hasElements(state) {
        return !!state.list.length;
    },
    list(state) {
        return deepClone(state.list);
    },
    filteredList(state) {
        const filteredList = state.filters.reduce((list, filter) => filter.apply(list), state.list);

        return deepClone(filteredList);
    },
    filters(state) {
        // Filter by type
        const uniqueTypes = [...new Set(state.list.map(item => item.type))];
        const filterByTypeOptions = [
            { value: "", label: "all" },
            ...uniqueTypes.map(type => ({ value: type, label: type })),
        ];
        const filterByType = {
            key: "type",
            options: filterByTypeOptions,
            value: state.filters.find(filter => filter.key === "type")?.value,
        };

        // Filter by createdBy
        const uniqueCreators = state.list
            .map(item => item.createdBy)
            .filter(Boolean)
            .filter((user, index, users) => index === users.findIndex(u => u._id === user._id));
        const filterByCreatedByOptions = [
            { value: "", label: "any" },
            ...uniqueCreators.map(user => ({
                value: user._id,
                label: "",
                text: user.firstname + " " + user.lastname,
            })),
        ];
        const filterByCreatedBy = {
            key: "createdBy",
            options: filterByCreatedByOptions,
            value: state.filters.find(filter => filter.key === "createdBy")?.value,
        };

        // Filter by usedInPaths
        const uniquePaths = state.list
            .map(item => item.usedInPaths)
            .filter(item => item.length)
            .flat()
            .filter(
                (path, index, paths) =>
                    index === paths.findIndex(p => p.commonId === path.commonId),
            );

        const filterByUsedInPathsOptions = [
            { value: "", label: "any" },
            { value: "none", label: "none" },
            ...uniquePaths.map(path => ({
                value: path.commonId,
                label: "",
                text: path.name,
            })),
        ];

        const filterByUsedInPaths = {
            key: "usedInPaths",
            options: filterByUsedInPathsOptions,
            value: state.filters.find(filter => filter.key === "usedInPaths")?.value,
        };

        return deepClone([filterByType, filterByCreatedBy, filterByUsedInPaths]);
    },
    search(state) {
        return (query, { useFilteredList = true } = {}) => {
            let list = getters.list(state);
            if (useFilteredList) {
                list = getters.filteredList(state);
            }

            return deepClone(
                list.filter(item => item.name.toLowerCase().includes(query.toLowerCase())),
            );
        };
    },
    details(state) {
        return id => deepClone(state.details[id] || { commonId: id, ...defaultElement });
    },
};

const actions = {
    getList({ commit }) {
        return api.get("/api/learning/element").then(res => {
            const list = res.data && res.data.items ? res.data.items : [];
            commit("updateList", {
                // Apply default sorting before saving in `state`
                list: list.sort(alphabetically("name")),
            });
        });
    },
    get({ commit }, { payload: { id, type } }) {
        return api.get(`/api/learning/element/${type}/${id}`).then(res => {
            commit("updateDetails", {
                element: {
                    ...defaultElement,
                    ...res.data,
                },
            });
        });
    },
    update({ commit }, { payload: { element } }) {
        return api
            .put(`/api/learning/element/${element.type}/${element.commonId}`, element)
            .then(res => {
                commit("updateDetails", { element: res.data });
            });
    },
    delete({ commit }, { payload: { id } }) {
        return api.delete(`/api/learning/element/${id}`).then(() => {
            commit("deleteDetails", { id });
        });
    },
    filterBy({ commit, state }, { payload: { key, value } }) {
        const filters = state.filters.map(filter => {
            if (filter.key === key) {
                return { ...filter, value };
            }

            return filter;
        });

        commit("updateFilters", { filters });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateDetails(state, { element }) {
        state.details = {
            ...state.details,
            [element.commonId]: element,
        };
    },
    deleteDetails(state, { id }) {
        delete state.details[id];
    },
    updateFilters(state, { filters }) {
        state.filters = [...filters];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export { filters as __test__filters };
